/** Sign-in, two-factor, password reset, sign-up and email verification copy. Part of `UI_STRINGS` - see ui-strings.ts. */
export const UI_AUTH_STRINGS = {
  AUTH: {
    LOGIN_HEADING: 'Welcome back!',
    LOGIN_SUBTITLE: 'Log in to your My NEXT Raise account',
    LOGIN_BUTTON: 'Log in',
    LOGIN_SUBMITTING: 'Logging in…',
    NO_ACCOUNT: "Don't have an account?",
    SIGN_UP: 'Sign up',
    FORGOT_PASSWORD: 'Forgot password?',
    SECURE_BADGE: 'Your login is protected with secure encryption',
    LOGIN_ERROR: 'Login failed. Please check your details and try again.',
  },
  /**
   * Second-factor step of login (FR-04, MYN-139). Copy is split by whether the
   * factor has a delivery step: `SENT_*`, `RESEND*` and the expiry countdown
   * belong to `email_otp` only. An authenticator app has nothing to resend, so
   * `TOTP_*` deliberately has no counterpart for any of them
   * (`docs/specs/features/identity-access/02-design.md` §11 decision 4).
   */
  TWO_FACTOR: {
    BADGE: 'Security Check - Step 2 of 2',
    HEADING_PREFIX: 'Verify your ',
    HEADING_HIGHLIGHT: 'identity',
    EMAIL_OTP_SUBTITLE_PREFIX: "We've sent a 6-digit code to",
    EMAIL_OTP_SUBTITLE_LINE2: 'Enter the code below to finish signing in.',
    TOTP_SUBTITLE: 'Open your authenticator app and enter the 6-digit code it shows.',
    SENT_TO_LABEL: 'CODE SENT TO',
    SENT_TO_HINT: 'Check your inbox and spam folder',
    SENT_CHIP: 'Sent',
    CARD_TITLE: 'Enter Verification Code',
    EMAIL_OTP_CARD_SUBTITLE: '6-digit one-time password',
    TOTP_CARD_SUBTITLE: '6-digit code from your authenticator app',
    CODE_LABEL: 'Verification code',
    EXPIRES_NOTE_PREFIX: 'This code expires in ',
    EXPIRES_NOTE_BOLD: '10 minutes',
    EXPIRES_NOTE_SUFFIX: '. Do not share it with anyone.',
    EXPIRES_IN: 'Code expires in',
    NO_CODE: "Didn't receive it?",
    RESEND: 'Resend Code',
    RESENDING: 'Sending…',
    RESENT: 'A new code is on its way.',
    RESEND_ERROR: 'We could not resend the code. Please try again.',
    ERROR_RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
    BACK: 'Back to log in',
    SUBMIT: 'Verify & Continue',
    SUBMITTING: 'Verifying…',
    ERROR: 'That code is invalid or expired. Please try again.',
    ERROR_INVALID: 'That code is incorrect. Please try again.',
    ERROR_EXPIRED: 'That code has expired. Request a new one.',
    ERROR_LOCKED: 'Too many attempts. Please try again later.',
    ENCRYPTION_NOTE: 'Your data is encrypted and never shared without consent.',
  },
  /**
   * Member-facing password reset (FR-05b, MYN-133). One route, four states
   * (`ForgotPasswordStep`): request -> code -> password -> failed. `SENT_ACK`
   * is the neutral acknowledgement shown as the code step's subtitle - it is
   * deliberately non-conditional and must never confirm the address is
   * registered.
   */
  FORGOT_PASSWORD: {
    // request
    BADGE: 'Password Reset',
    HEADING_PREFIX: 'Reset your ',
    HEADING_HIGHLIGHT: 'password',
    SUBTITLE: 'Enter the email address on your account and we will send you a reset code.',
    CARD_TITLE: 'Forgot Password',
    CARD_SUBTITLE: 'Enter your account email',
    EMAIL_LABEL: 'Email address',
    EMAIL_PLACEHOLDER: 'Email address',
    EMAIL_REQUIRED: 'Email is required',
    EMAIL_INVALID: 'Please enter a valid email address',
    SUBMIT: 'Send Reset Code',
    SUBMITTING: 'Sending…',
    BACK_TO_LOGIN: 'Back to log in',
    REQUEST_ERROR: 'We could not reach the server. Please try again.',
    // code (OTP entry + resend; client-side only, no verify call - see
    // ForgotPasswordPage's handleContinueToPassword)
    // acknowledgement (never confirms the address is registered) - the
    // subtitle on the code step itself, since the interstitial that used to
    // carry it was collapsed away
    SENT_ACK: 'If an account exists for that address, a password reset code has been sent to it.',
    CODE_HEADING_PREFIX: 'Enter your ',
    CODE_HEADING_HIGHLIGHT: 'code',
    CODE_LABEL: 'Reset code',
    CODE_REQUIRED: 'Enter the 6-digit code',
    CODE_CONTINUE: 'Continue',
    NO_CODE: "Didn't receive it?",
    RESEND: 'Resend Code',
    RESENDING: 'Sending…',
    RESENT: 'A new code is on its way if the address is registered.',
    USE_DIFFERENT_EMAIL: 'Use a different email',
    // password (new password + confirm; the step that submits)
    RESET_BADGE: 'Set a New Password',
    RESET_HEADING_PREFIX: 'Set a new ',
    RESET_HEADING_HIGHLIGHT: 'password',
    NEW_PASSWORD_LABEL: 'New password',
    NEW_PASSWORD_PLACEHOLDER: '••••••••',
    NEW_PASSWORD_MIN: 'Password must be at least 8 characters',
    CONFIRM_NEW_PASSWORD_LABEL: 'Confirm new password',
    CONFIRM_NEW_PASSWORD_REQUIRED: 'Please confirm your password',
    PASSWORD_MISMATCH: 'Passwords do not match',
    RESET_SUBMIT: 'Reset Password',
    RESET_SUBMITTING: 'Resetting…',
    RESET_ERROR: 'We could not reset your password. Please try again.',
    // failed (one state for every OTP failure - never reveals which)
    FAILED_HEADING: 'That Code No Longer Works',
    FAILED_BODY:
      'That code is incorrect, expired, or has already been used. Request a new code to keep resetting your password.',
    FAILED_CTA: 'Request a New Code',
    // done (terminal confirmation, rendered in place of the reset form - no auto-sign-in)
    DONE_HEADING: 'Password Reset',
    DONE_BODY:
      'Your password has been reset and you have been signed out everywhere for your security. Please sign in again with your new password.',
    DONE_CTA: 'Go to Log In',
    ENCRYPTION_NOTE: 'Your data is encrypted and never shared without consent.',
  },
  SIGNUP: {
    BADGE: 'Welcome to My NEXT Raise',
    HEADING_PREFIX: "Let's set up your ",
    HEADING_HIGHLIGHT: 'profile',
    SUBTITLE:
      'Tell us a little about yourself so we can personalize your experience on the platform.',
    CARD_TITLE: 'Personal Information',
    CARD_SUBTITLE: 'Your basic contact and identity details',
    FULL_NAME_LABEL: 'Full Name',
    FULL_NAME_PLACEHOLDER: 'Alex Johnson',
    EMAIL_LABEL: 'Email Address',
    EMAIL_PLACEHOLDER: 'alex.johnson@example.com',
    PASSWORD_LABEL: 'Password',
    PASSWORD_PLACEHOLDER: '••••••••',
    CONFIRM_PASSWORD_LABEL: 'Confirm Password',
    COMPANY_NAME_LABEL: 'Company Name',
    COMPANY_NAME_PLACEHOLDER: 'Acme Corp',
    // FR-218: required on the Issue Report signup, which creates the startup from them.
    COMPANY_NAME_REQUIRED: 'Company name is required',
    COMPANY_WEBSITE_LABEL: 'Company Website',
    COMPANY_WEBSITE_PLACEHOLDER: 'acmecorp.com',
    COMPANY_WEBSITE_REQUIRED: 'Company website is required',
    ERROR_COMPANY_DETAILS: 'Enter your company name and website to continue.',
    // The Issue Report signup's own words (approved journey screens, 2026-10-27).
    ISSUE_REPORT_HEADING_PREFIX: 'Register to get your ',
    ISSUE_REPORT_HEADING_HIGHLIGHT: 'Issue Report',
    ISSUE_REPORT_CARD_TITLE: 'Your details',
    ISSUE_REPORT_CARD_SUBTITLE: 'The Issue Report is bought for your startup, so we need your company email.',
    ISSUE_REPORT_EMAIL_LABEL: 'Company Email',
    ISSUE_REPORT_EMAIL_HINT:
      "Use your work address. Gmail, Outlook and other personal addresses can't be used for an Issue Report.",
    COMPANY_WEBSITE_HINT: 'No need to type https://',
    ISSUE_REPORT_SUBMIT: 'Create account',
    ENCRYPTION_NOTE: 'Your data is encrypted and never shared without consent.',
    CONSENT_LABEL_PREFIX: 'I agree to the ',
    CONSENT_LABEL_LINK: 'Terms and Conditions',
    CONSENT_LABEL_SUFFIX: '.',
    CONSENT_REQUIRED_ERROR: 'You must accept the Terms and Conditions to continue.',
    SUBMIT: 'Submit',
    SUBMITTING: 'Creating your account…',
    ERROR: 'We could not create your account. Please try again.',
    ERROR_EMAIL_EXISTS: 'An account with this email already exists. Please log in instead.',
    ERROR_EMAIL_UNAVAILABLE: "This email can't be used to create an account. Please contact support.",
    /** FR-207: exact wording. Only a signup that came from the Issue Report CTA is refused a personal email. */
    ERROR_ISSUE_REPORT_COMPANY_EMAIL:
      'Access to the Issue Report product requires a verified company email.',
    /** Shown only on a signup that carries the Issue Report intent, so a visitor who has an account can go back (FR-206). */
    HAVE_ACCOUNT: 'Already have an account?',
    LOG_IN: 'Log in',
  },
  VERIFY_EMAIL: {
    BADGE: 'Security Check - Step 2 of 3',
    HEADING_PREFIX: 'Verify your ',
    HEADING_HIGHLIGHT: 'email',
    SUBTITLE_PREFIX: "We've sent a 6-digit code to",
    SUBTITLE_LINE2: 'Enter the code below to secure your account.',
    SENT_TO_LABEL: 'CODE SENT TO',
    SENT_TO_HINT: 'Check your inbox and spam folder',
    SENT_CHIP: 'Sent',
    SEND_FAILED:
      'We could not send your code right now. Please use Resend Code to try again in a moment.',
    CARD_TITLE: 'Enter Verification Code',
    CARD_SUBTITLE: '6-digit one-time password',
    CODE_LABEL: 'Verification code',
    EXPIRES_NOTE_PREFIX: 'This code expires in ',
    EXPIRES_NOTE_BOLD: '10 minutes',
    EXPIRES_NOTE_SUFFIX: '. Do not share it with anyone.',
    EXPIRES_IN: 'Code expires in',
    NO_CODE: "Didn't receive it?",
    RESEND: 'Resend Code',
    RESENDING: 'Sending…',
    RESENT: 'A new code is on its way if the address is registered.',
    RESEND_ERROR: 'We could not resend the code. Please try again.',
    ERROR_RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
    BACK: 'Back',
    SUBMIT: 'Verify & Continue',
    SUBMITTING: 'Verifying…',
    ERROR: 'That code is invalid or expired. Please try again.',
    ERROR_INVALID: 'That code is incorrect. Please try again.',
    ERROR_EXPIRED: 'That code has expired. Request a new one.',
    ERROR_LOCKED: 'Too many attempts. Request a new code.',
    ENCRYPTION_NOTE: 'Your data is encrypted and never shared without consent.',
  },
  EMAIL_VERIFIED: {
    BADGE: 'EMAIL VERIFIED',
    HEADING_PREFIX: 'Email Verified ',
    HEADING_HIGHLIGHT: 'Successfully!',
    SUBTITLE:
      'Your account is now active. You can now proceed to set up your profile and explore the platform.',
    TILE_ACTIVE_TITLE: 'Account Active',
    TILE_ACTIVE_DESC: 'Your account is ready to use',
    TILE_SECURED_TITLE: 'Secured',
    TILE_SECURED_DESC: 'End-to-end data encryption enabled',
    TILE_GROW_TITLE: 'Ready to Grow',
    TILE_GROW_DESC: 'Your raise journey starts now',
    CONTINUE: 'Go to Dashboard',
    ENCRYPTION_NOTE: 'Your data is encrypted and never shared without consent.',
    // Registration's sign-in was held, so setup is next with no sign-in (FR-217).
    SETUP_SUBTITLE:
      'Your account is now active. Next, protect it with two-step verification. You stay signed in, there is no need to log in again.',
    SETUP_NOTE:
      "You'll need an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password.",
    SETUP_CTA: 'Continue to two-step verification',
  },
} as const;
